import {
  Box,
  Button,
  CardMedia,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  alpha,
  Paper,
} from "@mui/material";
import React from "react";
import {
  CalendarMonthOutlined,
  CopyAllOutlined,
  Language,
  OpenInNew,
  VerifiedUser,
} from "@mui/icons-material";
import moment from "moment";
import ActionButtonDall from "./ActionButtonDall";
import ActionButtonCRM from "./ActionButtonCRM";
import WhatsCRM from "./addons/WhatsCRM";
import ActionButtonWhatsHam from "./action/ActionButtonWhatsHam";
import WhatsHAMAddon from "./addons/WhatsHAMAddon";
import ActionButtonSonivo from "./action/ActionButtonSonivo";
import WhatsCRMNotes from "./notes/WhatsCRMNotes";
import SonivoNotes from "./notes/SonivoNotes";
import WhatsHAMNotes from "./notes/WhatsHAMNotes";
import WhatsCRMAiAction from "./action/WhatsCRMAiAction";
import ActionInf from "./aiinf/ActionInf";
import WaCRMBundle from "./wacrm-bundle/WaCRMBundle";

const ProductList = ({
  data,
  hitAxios,
  getProducts,
  products,
  i,
  key,
  setValue,
  buttonDeploy,
}) => {
  const theme = useTheme();

  return (
    <Paper
      key={key}
      elevation={0}
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          transform: "translateY(-4px)",
        },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.08,
          )}, ${alpha(theme.palette.secondary.main, 0.05)})`,
          p: 3,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box
            sx={{
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: -3,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                opacity: 0.3,
              },
            }}
          >
            <CardMedia
              src={i?.product_logo}
              component="img"
              sx={{
                height: 80,
                width: 80,
                borderRadius: 2.5,
                position: "relative",
                zIndex: 1,
                boxShadow: `0 4px 16px ${alpha(
                  theme.palette.common.black,
                  0.15,
                )}`,
              }}
            />
          </Box>

          <Stack spacing={1.5} flex={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Typography variant="h6" fontWeight={700}>
                {i?.product_name}
              </Typography>
            </Stack>

            {/* Purchase Code */}
            <Paper
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 0.8,
                bgcolor: alpha(theme.palette.common.black, 0.05),
                borderRadius: 2,
                maxWidth: "fit-content",
              }}
            >
              <Typography
                variant="caption"
                fontFamily="monospace"
                fontWeight={600}
              >
                {i?.purchase_code}
              </Typography>
              <IconButton
                size="small"
                onClick={() =>
                  window.navigator.clipboard.writeText(i?.purchase_code)
                }
                sx={{
                  p: 0.5,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <CopyAllOutlined sx={{ fontSize: 14 }} />
              </IconButton>
            </Paper>

            {/* Meta Info */}
            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <CalendarMonthOutlined
                  sx={{ fontSize: 16, color: "text.secondary" }}
                />
                <Typography variant="caption" color="text.secondary">
                  {moment(i?.purchase_date).format("DD MMM YY")}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={0.5} alignItems="center">
                <Language sx={{ fontSize: 16, color: "text.secondary" }} />
                <Stack direction="row" spacing={0.2} alignItems="center">
                  <Typography
                    variant="caption"
                    color="primary.main"
                    fontWeight={600}
                    sx={{ cursor: "pointer" }}
                    onClick={() => window.open(`https://${i?.install_domain}`)}
                  >
                    {i?.install_domain}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => window.open(`https://${i?.install_domain}`)}
                    sx={{ p: 0.3 }}
                  >
                    <OpenInNew sx={{ fontSize: 14 }} />
                  </IconButton>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* Content Section */}
      <Box p={3}>
        <Stack spacing={3}>
          {/* Notes */}
          {i?.product_id == 51122205 && <WhatsCRMNotes />}
          {i?.product_id == 40727633 && <WhatsHAMNotes />}
          {i?.product_id == 55533411 && <SonivoNotes />}

          {/* WhatsCRM Main App - product_id: 51122205 */}
          {i?.product_id == 51122205 && (
            <>
              <Divider />
              <ActionButtonCRM
                buttonDeploy={buttonDeploy}
                setValue={setValue}
                data={data}
                i={i}
                hitAxios={hitAxios}
              />
              <Divider />
              <WhatsCRM
                buttonDeploy={buttonDeploy}
                setValue={setValue}
                data={data}
                i={i}
                hitAxios={hitAxios}
              />
            </>
          )}

          {/* AI Chat Addon - product_id: 52498511 */}
          {i?.product_id == 52498511 && (
            <>
              <Divider />
              <WhatsCRMAiAction
                buttonDeploy={buttonDeploy}
                setValue={setValue}
                data={data}
                i={i}
                hitAxios={hitAxios}
              />
            </>
          )}

          {/* QR Code Login Addon - product_id: 57356510 */}
          {i?.product_id == 57356510 && (
            <>
              <Divider />
              <WhatsCRMAiAction
                buttonDeploy={buttonDeploy}
                setValue={setValue}
                data={data}
                i={i}
                hitAxios={hitAxios}
              />
            </>
          )}

          {/* Embed Login Addon - product_id: 61451340 */}
          {i?.product_id == 61451340 && (
            <>
              <Divider />
              <WhatsCRMAiAction
                buttonDeploy={buttonDeploy}
                setValue={setValue}
                data={data}
                i={i}
                hitAxios={hitAxios}
              />
            </>
          )}

          {/* Webhook Automation Addon - product_id: 58747894 */}
          {i?.product_id == 58747894 && (
            <>
              <Divider />
              <WhatsCRMAiAction
                buttonDeploy={buttonDeploy}
                setValue={setValue}
                data={data}
                i={i}
                hitAxios={hitAxios}
              />
            </>
          )}

          {/* WhatsApp Calling - product_id: 61890622 */}
          {i?.product_id == 61890622 && (
            <>
              <Divider />
              <WhatsCRMAiAction
                buttonDeploy={buttonDeploy}
                setValue={setValue}
                data={data}
                i={i}
                hitAxios={hitAxios}
              />
            </>
          )}

          {/* WhatsHAM - product_id: 40727633 */}
          {i?.product_id == 40727633 && (
            <>
              <Divider />
              <WhatsHAMAddon
                buttonDeploy={buttonDeploy}
                setValue={setValue}
                data={data}
                i={i}
                hitAxios={hitAxios}
              />
              <Divider />
              <ActionButtonWhatsHam
                buttonDeploy={buttonDeploy}
                setValue={setValue}
                data={data}
                i={i}
                hitAxios={hitAxios}
              />
            </>
          )}

          {/* DallHam - product_id: 43648878 */}
          {i?.product_id == 43648878 && (
            <>
              <Divider />
              <ActionButtonDall
                setValue={setValue}
                data={data}
                i={i}
                hitAxios={hitAxios}
              />
            </>
          )}

          {/* Sonivo - product_id: 55533411 */}
          {i?.product_id == 55533411 && (
            <>
              <Divider />
              <ActionButtonSonivo
                buttonDeploy={buttonDeploy}
                setValue={setValue}
                data={data}
                i={i}
                hitAxios={hitAxios}
              />
            </>
          )}

          {/* Ai Influencer - product_id: 55533411 */}
          {i?.product_id == 62472601 && (
            <>
              <Divider />
              <ActionInf
                buttonDeploy={buttonDeploy}
                setValue={setValue}
                data={data}
                i={i}
                hitAxios={hitAxios}
              />
            </>
          )}

          {/* wacrm Bundle - product_id: 63933531 */}
          {i?.product_id == 63933531 && (
            <>
              <Divider />
              <WaCRMBundle
                buttonDeploy={buttonDeploy}
                setValue={setValue}
                data={data}
                i={i}
                hitAxios={hitAxios}
              />
            </>
          )}
        </Stack>
      </Box>
    </Paper>
  );
};

export default ProductList;
